(() => {
  const GATE_ATTRIBUTE = 'data-explicit-option-positions';
  const STICKY_FORM_SELECTOR =
    'form[data-essential-cart-element="essential-sticky-add-to-cart-form"]';
  const STICKY_BUTTON_SELECTOR =
    'button[data-essential-cart-element="add-to-cart-button"]';

  class ExplicitVariantSelectionGate {
    constructor(productForm) {
      this.productForm = productForm;
      this.form = productForm.querySelector('form[data-type="add-to-cart-form"]');
      this.productInfo = productForm.closest('product-info');
      this.variantPicker = this.productInfo?.querySelector('variant-selects');
      this.submitButton = this.form?.querySelector('button[name="add"]');
      this.chooseOptionsText = productForm.dataset.chooseOptionsText || 'Válassz a lehetőségek közül';
      this.optionPositions = (productForm.dataset.explicitOptionPositions || '')
        .split(',')
        .map((value) => Number.parseInt(value, 10))
        .filter(Number.isInteger);
      this.pending = false;
      this.stickyObserver = null;
    }

    initialize() {
      if (!this.form || !this.productInfo || !this.variantPicker || !this.submitButton) return;
      if (!this.optionPositions.length || this.hasExplicitVariantInUrl()) return;

      this.productForm.dataset.explicitVariantGateInitialized = 'true';
      this.clearExplicitOptionSelections();
      this.setPendingState();

      this.variantPicker.addEventListener('change', () => this.setPendingState());
      this.productInfo.addEventListener('change', (event) => this.handleVariantIdChange(event));
      this.form.addEventListener(
        'submit',
        (event) => {
          if (!this.pending) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.submitButton.focus();
        },
        true
      );
    }

    hasExplicitVariantInUrl() {
      const productPath = this.productInfo?.dataset.url;
      if (!productPath || new URL(productPath, window.location.origin).pathname !== window.location.pathname) {
        return false;
      }

      return new URLSearchParams(window.location.search).has('variant');
    }

    clearExplicitOptionSelections() {
      const optionGroups = Array.from(this.variantPicker.children).filter((element) =>
        element.matches('.product-form__input')
      );

      this.optionPositions.forEach((position) => {
        const group = optionGroups[position - 1];
        if (!group) return;

        group.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });

        group.querySelectorAll('select').forEach((select) => {
          Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
          select.selectedIndex = -1;
        });

        const selectedValue = group.querySelector('[data-selected-value]');
        if (selectedValue) selectedValue.textContent = '';
      });
    }

    setPendingState() {
      this.pending = true;
      this.setVariantInputs('');
      this.setMainSubmitState(true);
      this.setDynamicCheckoutVisibility(false);
      this.syncStickyCart(true);
      this.observeStickyCart();
    }

    handleVariantIdChange(event) {
      if (!event.target.matches('input[name="id"]')) return;

      const variantId = event.target.value;
      event.target.disabled = !variantId;

      queueMicrotask(() => {
        if (!variantId) {
          this.setPendingState();
          return;
        }

        if (this.submitButton.disabled) {
          this.setUnavailableVariantState();
          return;
        }

        this.releasePendingState(variantId);
      });
    }

    setUnavailableVariantState() {
      this.pending = true;
      const soldOutText =
        this.submitButton.querySelector(':scope > span')?.textContent.trim() || this.chooseOptionsText;
      this.setVariantInputs('');
      this.setDynamicCheckoutVisibility(false);
      this.syncStickyCart(true, '', soldOutText);
      this.observeStickyCart();
    }

    releasePendingState(variantId) {
      this.pending = false;
      this.setVariantInputs(variantId);
      this.setDynamicCheckoutVisibility(true);
      this.syncStickyCart(false, variantId);
      this.stickyObserver?.disconnect();
      this.stickyObserver = null;
    }

    setVariantInputs(variantId) {
      const sectionId = this.productInfo.dataset.section;
      const forms = this.productInfo.querySelectorAll(
        `#product-form-${sectionId}, #product-form-installment-${sectionId}`
      );

      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId;
        input.disabled = !variantId;
      });
    }

    setMainSubmitState(disabled) {
      this.submitButton.disabled = disabled;
      this.submitButton.toggleAttribute('aria-disabled', disabled);

      if (!disabled) return;
      const text = this.submitButton.querySelector(':scope > span');
      if (text) text.textContent = this.chooseOptionsText;
    }

    setDynamicCheckoutVisibility(visible) {
      this.form.querySelectorAll('.shopify-payment-button').forEach((container) => {
        if (!container.dataset.tmsOriginalHidden) {
          container.dataset.tmsOriginalHidden = container.hidden ? 'true' : 'false';
        }

        container.hidden = visible ? container.dataset.tmsOriginalHidden === 'true' : true;
      });
    }

    observeStickyCart() {
      if (this.stickyObserver) return;

      this.stickyObserver = new MutationObserver((mutations) => {
        if (!this.pending) return;

        const containsStickyCart = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === Node.ELEMENT_NODE &&
              (node.matches?.(`${STICKY_FORM_SELECTOR}, ${STICKY_BUTTON_SELECTOR}`) ||
                node.closest?.(STICKY_FORM_SELECTOR) ||
                node.querySelector?.(`${STICKY_FORM_SELECTOR}, ${STICKY_BUTTON_SELECTOR}`))
          )
        );

        if (containsStickyCart) this.syncStickyCart(true);
      });

      this.stickyObserver.observe(document.body, { childList: true, subtree: true });
    }

    syncStickyCart(disabled, variantId = '', disabledText = this.chooseOptionsText) {
      document.querySelectorAll(STICKY_FORM_SELECTOR).forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        const button = form.querySelector(STICKY_BUTTON_SELECTOR);
        if (!button) return;

        form.dataset.tmsExplicitSelectionPending = disabled ? 'true' : 'false';
        if (input) {
          input.value = variantId;
          input.disabled = disabled || !variantId;
        }

        button.disabled = disabled;
        button.toggleAttribute('aria-disabled', disabled);
        button.classList.toggle('tms-explicit-selection-pending', disabled);
        if (disabled) {
          button.dataset.tmsChooseOptionsText = disabledText;
        } else {
          delete button.dataset.tmsChooseOptionsText;
        }
      });
    }
  }

  const manager = (window.TMSExplicitVariantSelection ||= {
    scan(root = document) {
      const forms = [];
      if (root.matches?.(`product-form[${GATE_ATTRIBUTE}]`)) forms.push(root);
      forms.push(...root.querySelectorAll?.(`product-form[${GATE_ATTRIBUTE}]`) || []);

      forms.forEach((productForm) => {
        if (productForm.dataset.explicitVariantGateInitialized === 'true') return;
        new ExplicitVariantSelectionGate(productForm).initialize();
      });
    },
  });

  if (!manager.observer) {
    manager.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) =>
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) manager.scan(node);
        })
      );
    });
    manager.observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest?.(STICKY_BUTTON_SELECTOR);
        const form = button?.closest(STICKY_FORM_SELECTOR);
        if (form?.dataset.tmsExplicitSelectionPending !== 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  }

  manager.scan();
})();

